import { useController, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import FormField from '@/shared/components/FormField';
import FormNumberInput from '@/shared/components/FormNumberInput';
import StepHeading from '../StepHeading';
import { acceptsLaterDate, ConfigurationFormData } from '../formData';

interface Props {
  showAscFields: boolean;
  showCancellation: boolean;
  disabled: boolean;
}

export default function AppointmentRulesStep({
  showAscFields,
  showCancellation,
  disabled,
}: Props) {
  const intl = useIntl();
  const { control, register, formState } = useFormContext<ConfigurationFormData>();
  const minSameDayGap = useWatch({ control, name: 'minSameDayGap' });
  const allowCancellation = useWatch({ control, name: 'allowCancellation' });
  const {
    field: { value: worseDateValue, ...worseDateField },
  } = useController({ control, name: 'acceptWorseDate' });

  return (
    <div className="space-y-4">
      <div>
        <StepHeading
          labelId="accounts.configForm.noticeHeading"
          tooltipId="accounts.configForm.minDaysFromNowHelp"
        />
        <div className="md:w-1/2 md:pr-1.5">
          <FormField id="min-days-from-now" error={formState.errors.minDaysFromNow?.message}>
            <FormNumberInput
              {...register('minDaysFromNow', {
                min: {
                  value: 0,
                  message: intl.formatMessage({ id: 'accounts.configForm.minDaysFromNowMin' }),
                },
                max: {
                  value: 365,
                  message: intl.formatMessage({ id: 'accounts.configForm.minDaysFromNowMax' }),
                },
              })}
              id="min-days-from-now"
              min={0}
              max={365}
              placeholder={intl.formatMessage({
                id: 'accounts.configForm.minDaysFromNowPlaceholder',
              })}
              suffix={<FormattedMessage id="common.days" />}
              disabled={disabled}
              hasError={!!formState.errors.minDaysFromNow}
            />
          </FormField>
        </div>
      </div>

      {showAscFields && (
        <div className="grid gap-3 md:grid-cols-2">
          <FormField
            id="max-gap"
            labelId="accounts.configForm.maxGap"
            tooltipId="accounts.configForm.maxGapHelp"
            variant="plain"
            error={formState.errors.maxGapAscConsular?.message}
          >
            <FormNumberInput
              {...register('maxGapAscConsular', {
                min: {
                  value: 0,
                  message: intl.formatMessage({ id: 'accounts.configForm.maxGapMin' }),
                },
                max: {
                  value: 365,
                  message: intl.formatMessage({ id: 'accounts.configForm.maxGapMax' }),
                },
              })}
              id="max-gap"
              aria-describedby="max-gap-hint"
              min={0}
              max={365}
              placeholder={intl.formatMessage({ id: 'accounts.configForm.maxGapPlaceholder' })}
              suffix={<FormattedMessage id="common.days" />}
              disabled={disabled}
              hasError={!!formState.errors.maxGapAscConsular}
            />
            <p id="max-gap-hint" className="mt-1 text-xs text-slate-500">
              <FormattedMessage id="accounts.configForm.maxGapHint" />
            </p>
          </FormField>

          <div>
            <FormField
              id="min-same-day-gap"
              labelId="accounts.configForm.minSameDayGap"
              tooltipId="accounts.configForm.minSameDayGapHelp"
              variant="plain"
              error={formState.errors.minSameDayGap?.message}
            >
              <FormNumberInput
                {...register('minSameDayGap', {
                  min: {
                    value: 60,
                    message: intl.formatMessage({ id: 'accounts.configForm.minSameDayGapMin' }),
                  },
                  max: {
                    value: 480,
                    message: intl.formatMessage({ id: 'accounts.configForm.minSameDayGapMax' }),
                  },
                })}
                id="min-same-day-gap"
                min={60}
                max={480}
                placeholder={intl.formatMessage({
                  id: 'accounts.configForm.minSameDayGapPlaceholder',
                })}
                suffix={<FormattedMessage id="common.minutes" />}
                disabled={disabled}
                hasError={!!formState.errors.minSameDayGap}
              />
            </FormField>
            {Number(minSameDayGap) >= 60 && Number(minSameDayGap) < 120 && (
              <p className="mt-1 text-sm text-amber-600">
                <FormattedMessage id="accounts.configForm.minSameDayGapWarning" />
              </p>
            )}
          </div>
        </div>
      )}

      {showCancellation && (
        <>
          <div className="rounded-xl border border-slate-200 bg-white/70 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                {...register('allowCancellation')}
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                disabled={disabled}
              />
              <span className="text-sm font-medium text-slate-900">
                <FormattedMessage id="accounts.configForm.allowCancellation" />
              </span>
            </label>
            <p className="mt-2 ml-7 text-xs text-slate-500">
              <FormattedMessage id="accounts.configForm.allowCancellationHelp" />
            </p>
            <div className="mt-2 ml-7 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-xs text-amber-700">
                <FormattedMessage id="accounts.configForm.allowCancellationWarning" />
              </p>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white/70 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                {...worseDateField}
                checked={acceptsLaterDate({ allowCancellation, acceptWorseDate: worseDateValue })}
                onChange={(event) => worseDateField.onChange(event.target.checked)}
                type="checkbox"
                className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                disabled={disabled || allowCancellation}
              />
              <span className="text-sm font-medium text-slate-900">
                <FormattedMessage id="accounts.configForm.acceptWorseDate" />
              </span>
            </label>
            <p className="mt-2 ml-7 text-xs text-slate-500">
              <FormattedMessage id="accounts.configForm.acceptWorseDateHelp" />
            </p>
          </div>
        </>
      )}
    </div>
  );
}
